// ActionButtons.tsx
import React from "react";

export type HeaderElement = {
    id: string;
    type: string;
    tagName: string;
    data: Record<string, any>;
    className?: string;
    style?: React.CSSProperties;
    props?: Record<string, any>;
    children?: HeaderElement[];
    css?: string;
};

// Action button configuration interface
export interface ActionButtonConfig {
    action: "edit" | "remove" | "duplicate" | "settings";
    icon: string;
    hoverColor?: string;
    onClick?: string; // Event handler name
}

// Create individual action button
export const createActionButton = (
    parentId: string,
    config: ActionButtonConfig
): HeaderElement => {
    const buttonId = `${parentId}-${config.action}-button`;
    const iconId = `${buttonId}-icon`;

    return {
        id: buttonId,
        type: "ActionButton",
        tagName: "button",
        data: {
            action: config.action,
            icon: config.icon,
            parentId: parentId
        },
        className: `action-btn ${config.action}-btn`,
        props: {
            onClick: config.onClick || `handle${config.action.charAt(0).toUpperCase() + config.action.slice(1)}Click`
        },
        children: [
            {
                id: iconId,
                type: "Icon",
                tagName: "svg",
                data: {
                    icon: config.icon
                },
                style: {
                    width: "12",
                    height: "12",

                },
                className: "btn-icon",
                children: []
            }
        ]
    };
};

// Create hover actions container with action buttons
export const createHoverActionsContainer = (
    parentId: string,
    actions: ActionButtonConfig[] = [
        { action: "edit", icon: "setting" },
        { action: "remove", icon: "trash" }
    ]
): HeaderElement => {
    const containerId = `${parentId}-hover-actions`;

    return {
        id: containerId,
        type: "HoverActions",
        tagName: "div",
        data: {
            showOnHover: true,
            parentId: parentId
        },
        className: "hover-actions",
        style: {
            position: "absolute",
            bottom: '100%',
            right: "0"
        },
        children: actions.map(actionConfig => createActionButton(parentId, actionConfig))
    };
};

// Predefined action configurations
export const DEFAULT_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
];

export const EXTENDED_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
    // { action: "duplicate", icon: "📋" },
];

export const SETTINGS_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
];

// Component-specific action configurations
export const LOGO_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" }, // Image icon for logo editing
    { action: "remove", icon: "trash" }
];

export const SEARCHBAR_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
];

export const NAVIGATION_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
    // { action: "duplicate", icon: "📋" },
];

export const ICON_BUTTON_ACTIONS: ActionButtonConfig[] = [
    { action: "edit", icon: "setting" },
    { action: "remove", icon: "trash" }
];

// CSS for action buttons (to be included in parent components)
export const ACTION_BUTTONS_CSS = `
    /* Hover Actions Container */
    .hover-actions {
        position: absolute;
        bottom: 100%;
        right: 0;
        opacity: 0;
        background-color: rgba(255, 255, 255, 0.7);
        padding: 4px;
        border-radius: 4px;
        display: flex;
        transition: opacity 0.2s ease;
        z-index: 10;
    }
    
    /* Show on parent hover */
    .header-component:hover .hover-actions,
    .logo-container:hover .hover-actions,
    .searchbar-container:hover .hover-actions,
    .navitems-container:hover .hover-actions,
    .iconbutton-container:hover .hover-actions {
        opacity: 1;
    }
    
    /* Action Button Base */
    .action-btn {
        padding: 4px 8px;
        margin: 0 2px;
        color: white;
        background: none;
        border: none;
        cursor: pointer;
        border-radius: 2px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        min-height: 24px;
    }
    
    /* Action Button Hover Effects */
    .edit-btn:hover,
    .settings-btn:hover {
        color: #64b5f6;
        transform: scale(1.1);
        background-color: rgba(100, 181, 246, 0.1);
    }
    
    .remove-btn:hover {
        color: #f48fb1;
        transform: scale(1.1);
        background-color: rgba(244, 143, 177, 0.1);
    }
    
    .duplicate-btn:hover {
        color: #81c784;
        transform: scale(1.1);
        background-color: rgba(129, 199, 132, 0.1);
    }
    
    /* Button Icon */
    .btn-icon {
        display: inline-block;
        line-height: 1;
    }
    
    /* Focus states for accessibility */
    .action-btn:focus {
        outline: 2px solid rgba(255, 255, 255, 0.5);
        outline-offset: 1px;
    }
    
    /* Active states */
    .action-btn:active {
        transform: scale(0.95);
    }
`;

// Helper function to add hover actions to any element
export const addHoverActionsToElement = (
    element: HeaderElement,
    actions: ActionButtonConfig[] = DEFAULT_ACTIONS
): HeaderElement => {
    const hoverActions = createHoverActionsContainer(element.id, actions);

    return {
        ...element,
        children: [
            ...(element.children || []),
            hoverActions
        ],
        css: `${element.css || ''}\n\n${ACTION_BUTTONS_CSS}`
    };
};

// Event handler helpers for common actions
export const createActionEventHandlers = (
    onEdit?: (elementId: string) => void,
    onRemove?: (elementId: string) => void,
    onDuplicate?: (elementId: string) => void,
    onSettings?: (elementId: string) => void
) => ({
    handleEditClick: (e: React.MouseEvent, elementId?: string) => {
        e.stopPropagation();
        if (onEdit && elementId) onEdit(elementId);
    },
    handleRemoveClick: (e: React.MouseEvent, elementId?: string) => {
        e.stopPropagation();
        if (onRemove && elementId) onRemove(elementId);
    },
    handleDuplicateClick: (e: React.MouseEvent, elementId?: string) => {
        e.stopPropagation();
        if (onDuplicate && elementId) onDuplicate(elementId);
    },
    handleSettingsClick: (e: React.MouseEvent, elementId?: string) => {
        e.stopPropagation();
        if (onSettings && elementId) onSettings(elementId);
    }
});

// Utility function to create custom action configurations
export const createCustomActions = (
    includeEdit: boolean = true,
    includeDuplicate: boolean = false,
    includeSettings: boolean = false,
    includeRemove: boolean = true,
    customIcons?: {
        edit?: string;
        duplicate?: string;
        settings?: string;
        remove?: string;
    }
): ActionButtonConfig[] => {
    const actions: ActionButtonConfig[] = [];

    if (includeEdit) {
        actions.push({
            action: "edit",
            icon: customIcons?.edit || "⚙️"
        });
    }

    if (includeDuplicate) {
        actions.push({
            action: "duplicate",
            icon: customIcons?.duplicate || "📋"
        });
    }

    if (includeSettings) {
        actions.push({
            action: "settings",
            icon: customIcons?.settings || "🔧"
        });
    }

    if (includeRemove) {
        actions.push({
            action: "remove",
            icon: customIcons?.remove || "🗑️"
        });
    }

    return actions;
};

// Example usage function
export const getActionButtonsExample = () => {
    return `
// Example 1: Basic usage with default actions
const logoWithActions = createLogoElement({
    id: "my-logo",
    image: "/logo.png",
    size: "120px",
    hasHoverActions: true
    // Uses DEFAULT_ACTIONS automatically
});

// Example 2: Custom actions
const customActions = [
    { action: "edit", icon: "🖼️" },
    { action: "duplicate", icon: "📋" },
    { action: "remove", icon: "🗑️" }
];

const logoWithCustomActions = createLogoElement({
    id: "my-logo",
    image: "/logo.png",
    size: "120px",
    hasHoverActions: true,
    actions: customActions
});

// Example 3: Using predefined action sets
const navWithExtendedActions = createNavItemsElement({
    id: "my-nav",
    items: navItems,
    actions: NAVIGATION_ACTIONS // Includes edit, duplicate, remove
});

// Example 4: Creating custom action sets
const ecommerceActions = createCustomActions(
    true,  // includeEdit
    true,  // includeDuplicate
    false, // includeSettings
    true,  // includeRemove
    {
        edit: "✏️",
        duplicate: "📋",
        remove: "❌"
    }
);
    `;
};

export default {
    createActionButton,
    createHoverActionsContainer,
    addHoverActionsToElement,
    createActionEventHandlers,
    createCustomActions,
    DEFAULT_ACTIONS,
    EXTENDED_ACTIONS,
    SETTINGS_ACTIONS,
    LOGO_ACTIONS,
    SEARCHBAR_ACTIONS,
    NAVIGATION_ACTIONS,
    ICON_BUTTON_ACTIONS,
    ACTION_BUTTONS_CSS
};